import {
  clearGuideTourProgress,
  finishGuideTour,
  getGuideTourProgress,
  isGuideTourSeen,
  type GuideTourProgress,
} from '../persistence'
import type { GuideTourDefinition, GuideTourRegistry } from '../types'
import {
  isGuideTourProgressExhausted,
  countAvailableGuideTourSteps,
  findNextAvailableGuideTourStepIndex,
  getAvailableGuideTourStepNumber,
  resolveGuideTourActivationStepIndex,
  resolveGuideTourForRuntime,
} from '../utils/tourRuntime'
import {
  getGuideTourDefaultPriority,
  getGuideTourStepRoute,
  getGuideTourTier,
  isGuideTourAvailable,
} from './utils'
import { isGuideTourAnnouncementListed } from './activation'

export type GuideTourNotificationKind = 'interrupted' | 'announcement'

export type GuideTourNotificationItem = {
  kind: GuideTourNotificationKind
  tour: GuideTourDefinition
  currentStepIndex: number
  viewedStepCount: number
  currentStepNumber: number
  stepCount: number
  currentStepTitle: string
  currentStepRoute: string | null
  typeLabel: string
}

export const getGuideTourTypeLabel = (tour: GuideTourDefinition): string => {
  switch (getGuideTourTier(tour)) {
    case 'campaign':
      return 'Campanha'
    case 'onboard':
      return 'Onboarding'
    case 'feature':
    case 'page':
      return 'Novidade'
    default:
      return 'Novidade'
  }
}

const buildGuideTourNotificationItem = (
  tour: GuideTourDefinition,
  kind: GuideTourNotificationKind,
  currentStepIndex: number,
  viewedStepCount: number,
  now: Date
): GuideTourNotificationItem | null => {
  const step = tour.steps[currentStepIndex]

  if (!step) {
    return null
  }

  return {
    kind,
    tour,
    currentStepIndex,
    viewedStepCount,
    currentStepNumber: getAvailableGuideTourStepNumber(
      tour,
      currentStepIndex,
      now
    ),
    stepCount: countAvailableGuideTourSteps(tour, now),
    currentStepTitle: step.title,
    currentStepRoute: getGuideTourStepRoute(tour, step) ?? null,
    typeLabel: getGuideTourTypeLabel(tour),
  }
}

const reconcileFinishedGuideTourProgress = (
  tour: GuideTourDefinition,
  progress: GuideTourProgress,
  now: Date
): boolean => {
  if (!isGuideTourProgressExhausted(tour, progress, now)) {
    return false
  }

  finishGuideTour(tour.id, tour.version)
  return true
}

export const listGuideTourNotifications = (
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen,
  now: Date = new Date(),
  pathname: string = typeof window !== 'undefined'
    ? window.location.pathname
    : '/',
  notificationsRevision = 0
): GuideTourNotificationItem[] => {
  const items = registry.listGuideTours().flatMap(tour => {
    if (!isGuideTourAvailable(tour, now)) {
      return []
    }

    const activeTour = resolveGuideTourForRuntime(tour)

    if (isSeen(tour.id, tour.version)) {
      if (getGuideTourProgress(tour.id, tour.version)) {
        clearGuideTourProgress(tour.id, tour.version)
      }

      return []
    }

    const progress = getGuideTourProgress(tour.id, tour.version)

    if (progress) {
      if (reconcileFinishedGuideTourProgress(activeTour, progress, now)) {
        return []
      }

      const currentStepIndex = resolveGuideTourActivationStepIndex(
        activeTour,
        progress.currentStepIndex,
        now
      )

      if (currentStepIndex === null) {
        return []
      }

      const item = buildGuideTourNotificationItem(
        activeTour,
        'interrupted',
        currentStepIndex,
        progress.viewedStepCount,
        now
      )

      return item ? [item] : []
    }

    if (!tour.listInNotifications) {
      return []
    }

    if (!isGuideTourAnnouncementListed(tour, now, pathname)) {
      return []
    }

    const firstStepIndex = findNextAvailableGuideTourStepIndex(
      activeTour,
      0,
      now
    )

    if (firstStepIndex === null) {
      return []
    }

    const item = buildGuideTourNotificationItem(
      activeTour,
      'announcement',
      firstStepIndex,
      0,
      now
    )

    return item ? [item] : []
  })

  return sortGuideTourNotificationsByPriority(items, notificationsRevision)
}

const sortGuideTourNotificationsByPriority = (
  items: GuideTourNotificationItem[],
  notificationsRevision: number
): GuideTourNotificationItem[] =>
  [...items].sort((left, right) => {
    const priorityDelta =
      getGuideTourDefaultPriority(right.tour) -
      getGuideTourDefaultPriority(left.tour)

    if (priorityDelta !== 0) {
      return priorityDelta
    }

    if (notificationsRevision < 0) {
      return 0
    }

    return left.tour.id.localeCompare(right.tour.id)
  })

export const listInProgressGuideTours = (
  registry: GuideTourRegistry,
  isSeen: typeof isGuideTourSeen = isGuideTourSeen,
  now: Date = new Date()
): GuideTourNotificationItem[] =>
  listGuideTourNotifications(registry, isSeen, now).filter(
    item => item.kind === 'interrupted'
  )
